import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Chip,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import BiotechIcon from '@mui/icons-material/Biotech';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

// -- Tipos --------------------------------------------------
interface Kpis {
  total_agravos: number;
  cids_distintos: number;
  unidades_notificadoras: number;
  regioes_afetadas: number;
  novos_30_dias: number;
  novos_7_dias: number;
  surtos_ativos: number;
  surtos_criticos: number;
}
interface GrupoDist {
  codigo: string;
  nome: string;
  cor: string;
  faixa: string;
  total: number;
}
interface SerieItem {
  semana_inicio: string;
  grupo_codigo: string;
  grupo_nome: string;
  grupo_cor: string;
  total: number;
}
interface TopAgravo {
  codigo_cid: string;
  nome_cid: string;
  grupo_codigo: string;
  grupo_nome: string;
  grupo_cor: string;
  total: number;
}
interface IncidenciaRegiao {
  regiao_id: number;
  regiao_nome: string;
  casos: number;
  cids_distintos: number;
  unidades: number;
  populacao_estimada: number;
  taxa_por_10k: number | null;
}
interface Surto {
  id: number;
  codigo_cid: string;
  nome_cid: string;
  grupo_cid: string;
  grupo_nome: string;
  grupo_cor: string;
  regiao_id: number;
  regiao_nome: string;
  data_deteccao: string;
  semana_epi: number;
  ano: number;
  casos_periodo: number;
  media_historica: number;
  desvio_padrao: number;
  score_alerta: number;
  variacao_percentual: number;
  nivel_alerta: 'ATENCAO' | 'ALERTA' | 'CRITICO';
  descricao_ia: string;
}
interface DashboardData {
  kpis: Kpis;
  distribuicao: GrupoDist[];
  serie_temporal: SerieItem[];
  top_agravos: TopAgravo[];
  incidencia_regiao: IncidenciaRegiao[];
  surtos: Surto[];
}
interface GrupoCid {
  codigo_grupo: string;
  nome: string;
  faixa_cid: string;
  cor_hex: string;
  ordem: number;
}
interface Regiao {
  id: number;
  nome: string;
  populacao_estimada: number;
}

// -- Helpers ------------------------------------------------
const toISO = (d: Date): string => d.toISOString().split('T')[0];

const cor = (hex: string | undefined, fallback = '#1976d2') => hex || fallback;

const nivelAlertaCor: Record<string, string> = {
  CRITICO: '#d32f2f',
  ALERTA: '#f57c00',
  ATENCAO: '#fbc02d',
};

function pivotSerie(items: SerieItem[]): { data: Record<string, any>[]; grupos: { codigo: string; nome: string; cor: string }[] } {
  const grupos = new Map<string, { codigo: string; nome: string; cor: string }>();
  const map = new Map<string, Record<string, any>>();
  for (const it of items) {
    grupos.set(it.grupo_codigo, { codigo: it.grupo_codigo, nome: it.grupo_nome, cor: it.grupo_cor });
    if (!map.has(it.semana_inicio)) map.set(it.semana_inicio, { semana_inicio: it.semana_inicio });
    map.get(it.semana_inicio)![it.grupo_codigo] = Number(it.total ?? 0);
  }
  return { data: Array.from(map.values()), grupos: Array.from(grupos.values()) };
}

const MonitoramentoVigilanciaEpidemiologica: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const navigate = useNavigate();

  const hoje = new Date();
  const seisMesesAtras = new Date();
  seisMesesAtras.setMonth(seisMesesAtras.getMonth() - 12);

  const [data, setData] = useState<DashboardData | null>(null);
  const [grupos, setGrupos] = useState<GrupoCid[]>([]);
  const [regioes, setRegioes] = useState<Regiao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState(toISO(seisMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));
  const [filtroGrupo, setFiltroGrupo] = useState<string>('');

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  useEffect(() => {
    if (!municipioAtivo?.id) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [dashboard, grps, regs] = await Promise.all([
          monitoramentoService.getVigilanciaDashboard(municipioAtivo.id, dataInicio, dataFim),
          monitoramentoService.getVigilanciaGruposCid(municipioAtivo.id),
          monitoramentoService.getVigilanciaRegioes(municipioAtivo.id),
        ]);
        if (!cancelled) {
          setData(dashboard);
          setGrupos(grps);
          setRegioes(regs);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Erro ao carregar painel de Vigilancia Epidemiologica.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  const seriePivotada = useMemo(() => {
    if (!data?.serie_temporal) return { data: [], grupos: [] };
    const all = pivotSerie(data.serie_temporal);
    if (filtroGrupo) {
      return {
        data: all.data,
        grupos: all.grupos.filter(g => g.codigo === filtroGrupo),
      };
    }
    return all;
  }, [data, filtroGrupo]);

  const kpis = data?.kpis as Kpis | undefined;
  const surtos = data?.surtos || [];
  const distribuicao = data?.distribuicao || [];

  if (!municipioAtivo) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">Selecione um municipio para visualizar o painel.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={700} display="flex" alignItems="center" gap={1}>
            <CoronavirusIcon color="error" /> Vigilancia Epidemiologica
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Painel de agravos, incidencia regional e deteccao de surtos - {municipioAtivo.nome}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            href={`/api${monitoramentoService.getVigilanciaCsvUrl(municipioAtivo.id, {
              data_inicio: dataInicio, data_fim: dataFim,
              ...(filtroGrupo ? { grupo_cid: filtroGrupo } : {}),
            })}`}
            target="_blank"
            rel="noopener"
            size="small"
          >
            CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            href={`/api${monitoramentoService.getVigilanciaPdfUrl(municipioAtivo.id, municipioAtivo.nome, {
              data_inicio: dataInicio, data_fim: dataFim,
              ...(filtroGrupo ? { grupo_cid: filtroGrupo } : {}),
            })}`}
            target="_blank"
            rel="noopener"
            size="small"
          >
            PDF
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/monitoramento/vigilancia-epidemiologica/detalhes')}
            size="small"
          >
            Detalhes
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <DateRangeSelector
              dataInicio={dataInicio}
              dataFim={dataFim}
              onChange={handleDateChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Grupo CID</InputLabel>
              <Select
                value={filtroGrupo}
                label="Grupo CID"
                onChange={(e) => setFiltroGrupo(e.target.value)}
              >
                <MenuItem value=""><em>Todos os grupos</em></MenuItem>
                {grupos.map(g => (
                  <MenuItem key={g.codigo_grupo} value={g.codigo_grupo}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, background: g.cor_hex, marginRight: 8, borderRadius: 2 }} />
                    {g.nome} ({g.faixa_cid})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} sx={{ textAlign: 'right' }}>
            <ChatIAButton contexto="dashboard" dadosContexto={JSON.stringify(data)} />
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}>
          {loading ? <Skeleton variant="rounded" height={92} /> :
            <KpiCard titulo="Total agravos" valor={Number(kpis?.total_agravos ?? 0).toLocaleString('pt-BR')} icone={<BiotechIcon />} cor="#1976d2" />}
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {loading ? <Skeleton variant="rounded" height={92} /> :
            <KpiCard titulo="Novos (30 dias)" valor={Number(kpis?.novos_30_dias ?? 0).toLocaleString('pt-BR')} icone={<FiberNewIcon />} cor="#388e3c" />}
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {loading ? <Skeleton variant="rounded" height={92} /> :
            <KpiCard titulo="CIDs distintos" valor={Number(kpis?.cids_distintos ?? 0)} icone={<TrendingUpIcon />} cor="#0288d1" />}
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {loading ? <Skeleton variant="rounded" height={92} /> :
            <KpiCard titulo="Regioes afetadas" valor={Number(kpis?.regioes_afetadas ?? 0)} icone={<LocationCityIcon />} cor="#7b1fa2" />}
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {loading ? <Skeleton variant="rounded" height={92} /> :
            <KpiCard titulo="Surtos ativos" valor={Number(kpis?.surtos_ativos ?? 0)} icone={<WarningAmberIcon />} cor="#f57c00" />}
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {loading ? <Skeleton variant="rounded" height={92} /> :
            <KpiCard titulo="Surtos criticos" valor={Number(kpis?.surtos_criticos ?? 0)} icone={<ErrorIcon />} cor="#d32f2f" />}
        </Grid>
      </Grid>

      {/* Alertas de surto (RF-13.4) */}
      {!loading && surtos.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid #d32f2f' }}>
          <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
            <WarningAmberIcon color="error" /> Alertas de surto detectados pela IA
          </Typography>
          <Grid container spacing={1.5}>
            {surtos.slice(0, 6).map(s => (
              <Grid item xs={12} md={6} lg={4} key={s.id}>
                <Paper variant="outlined" sx={{ p: 1.5, borderColor: nivelAlertaCor[s.nivel_alerta] || '#9e9e9e' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Chip
                      label={s.nivel_alerta}
                      size="small"
                      sx={{ bgcolor: nivelAlertaCor[s.nivel_alerta], color: 'white', fontWeight: 700 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Semana {s.semana_epi}/{s.ano}
                    </Typography>
                  </Stack>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {s.codigo_cid} - {s.nome_cid}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {s.grupo_nome} | {s.regiao_nome || 'Sem regional'}
                  </Typography>
                  <Box mt={0.8}>
                    <Typography variant="caption" display="block">
                      <strong>{s.casos_periodo}</strong> casos | media historica: {Number(s.media_historica).toFixed(1)} | score: {Number(s.score_alerta).toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="error" display="block">
                      Variacao: +{Number(s.variacao_percentual).toFixed(0)}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ mt: 0.8, display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                    {s.descricao_ia}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Distribuicao por grupo (PieChart) + Top agravos (BarChart) */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Distribuicao por grupo CID</Typography>
            {loading ? <Skeleton variant="rounded" height={300} /> : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={distribuicao}
                    dataKey="total"
                    nameKey="nome"
                    cx="50%" cy="50%"
                    outerRadius={110}
                    label={(d: any) => `${d.nome}`}
                  >
                    {distribuicao.map((d, idx) => (
                      <Cell key={idx} fill={cor(d.cor)} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => Number(v).toLocaleString('pt-BR')} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Top 10 agravos (CID)</Typography>
            {loading ? <Skeleton variant="rounded" height={300} /> : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={data?.top_agravos || []} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="codigo_cid" type="category" width={80} />
                  <Tooltip
                    formatter={(v: any) => Number(v).toLocaleString('pt-BR')}
                    labelFormatter={(label: any, payload: any) => {
                      const item = payload?.[0]?.payload as TopAgravo | undefined;
                      return item ? `${label} - ${item.nome_cid}` : label;
                    }}
                  />
                  <Bar dataKey="total" name="Casos">
                    {(data?.top_agravos || []).map((t, i) => (
                      <Cell key={i} fill={cor(t.grupo_cor)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Serie temporal */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Evolucao semanal por grupo CID</Typography>
        {loading ? <Skeleton variant="rounded" height={300} /> : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={seriePivotada.data} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="semana_inicio" />
              <YAxis />
              <Tooltip />
              <Legend />
              {seriePivotada.grupos.map((g) => (
                <Line
                  key={g.codigo}
                  type="monotone"
                  dataKey={g.codigo}
                  name={g.nome}
                  stroke={cor(g.cor)}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Incidencia regional (RF-13.3) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Incidencia por regional (taxa por 10.000 habitantes)</Typography>
        {loading ? <Skeleton variant="rounded" height={300} /> : (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data?.incidencia_regiao || []} margin={{ top: 5, right: 30, left: 10, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="regiao_nome" angle={-25} textAnchor="end" interval={0} height={70} />
                <YAxis />
                <Tooltip
                  formatter={(v: any, name: any) =>
                    name === 'taxa_por_10k'
                      ? [Number(v).toFixed(2), 'Taxa/10k'] as [string, string]
                      : [Number(v).toLocaleString('pt-BR'), String(name)] as [string, string]
                  }
                />
                <Legend />
                <Bar dataKey="taxa_por_10k" name="Taxa por 10k hab" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>

            <Box sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Regional</TableCell>
                    <TableCell align="right">Casos</TableCell>
                    <TableCell align="right">CIDs</TableCell>
                    <TableCell align="right">Unidades</TableCell>
                    <TableCell align="right">Populacao</TableCell>
                    <TableCell align="right">Taxa/10k</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.incidencia_regiao || []).map(r => (
                    <TableRow key={r.regiao_id} hover>
                      <TableCell>{r.regiao_nome}</TableCell>
                      <TableCell align="right">{Number(r.casos).toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">{r.cids_distintos}</TableCell>
                      <TableCell align="right">{r.unidades}</TableCell>
                      <TableCell align="right">{Number(r.populacao_estimada).toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">
                        <strong>{r.taxa_por_10k != null ? Number(r.taxa_por_10k).toFixed(2) : '-'}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Paper>

      {data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="vigilancia-epidemiologica"
          contexto="dashboard"
        />
      )}
    </Box>
  );
};

export default MonitoramentoVigilanciaEpidemiologica;
